import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { GoogleAuthButton } from "./GoogleAuthButton";
import { Separator } from "./ui/separator";
import { Eye, EyeOff } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";
import {
  identifyUser,
  setUserPropertiesOnce,
  setUserProperties,
  trackEvent,
  trackApiErrorToast,
} from "@/lib/mixpanel-client";

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pendingUploadId?: string;
}

export function AuthDialog({
  isOpen,
  onClose,
  onSuccess,
  pendingUploadId,
}: AuthDialogProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">("signup");
  const { toast } = useToast();
  const supabase = createClient();
  const t = useTranslations("Auth");
  const locale = useLocale();

  // Handle dialog close without signing out
  // This ensures we don't remove the user's session if they just close the dialog
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Track initiation of signup or login
      if (mode === "signup") {
        trackEvent("Signup Initiated", {
          Source: "Email Form",
          Method: "Email",
        });
      } else {
        trackEvent("Login Initiated", {
          Method: "Email",
        });
      }

      // Store the pending upload ID if it exists, just like we do with Google auth
      if (pendingUploadId) {
        // Store only in sessionStorage
        sessionStorage.setItem("pendingAuthUpload", pendingUploadId);

        console.log(
          "Stored pending upload ID for post-auth processing:",
          pendingUploadId
        );
      }

      // Save existing pendingAuthUpload ID if it exists but wasn't provided as a prop
      const existingPendingId = sessionStorage.getItem("pendingAuthUpload");

      let result;
      if (mode === "signup") {
        result = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback?locale=${locale}`,
            data: {
              email_confirmed: false,
              lang: locale,
            },
          },
        });

        if (result.error) {
          throw result.error;
        }

        if (result.data.user) {
          const signInResult = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (signInResult.error) {
            throw signInResult.error;
          }

          await supabase.from("profiles").upsert({
            id: signInResult.data.user.id,
            email_confirmed: false,
            email: email,
            updated_at: new Date().toISOString(),
          });

          // Track user sign up with Mixpanel
          const userId = signInResult.data.user.id;
          identifyUser(userId);
          setUserPropertiesOnce({
            $email: email,
            $created: new Date().toISOString(),
            "Plan Type": "free",
          });
          setUserProperties({
            "Last Login At": new Date().toISOString(),
          });
          trackEvent("User Signed Up", {
            Method: "Email",
            "Signup Date": new Date().toISOString(),
          });

          // Also track this as the first login
          trackEvent("User Logged In", {
            Method: "Email",
            "Is First Login": true,
          });

          toast({
            title: t("accountCreated"),
            description: t("verificationEmailSent"),
          });

          // Restore the pendingAuthUpload ID if it was saved earlier
          if (existingPendingId && !pendingUploadId) {
            sessionStorage.setItem("pendingAuthUpload", existingPendingId);
          }

          // Call onSuccess and redirect to dashboard instead of checking returnAfterAuthTo
          onSuccess();
          window.location.href = `${window.location.origin}/dashboard?pendingUpload=true`;
          return;
        }
      } else {
        result = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (result.error) {
          throw result.error;
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          // Check if this might be a first login after signup
          const user = session.user;

          // If created_at is very close to last_sign_in_at, it's likely the first login after signup
          // Using 10-minute threshold as a reasonable buffer
          const createdAt = new Date(user.created_at || "");
          const lastSignIn = new Date(user.last_sign_in_at || "");
          const timeDiffMinutes =
            Math.abs(lastSignIn.getTime() - createdAt.getTime()) / 60000;
          const isFirstLogin = timeDiffMinutes < 10;

          // Track user login with Mixpanel
          const userId = session.user.id;
          identifyUser(userId);
          setUserPropertiesOnce({ $email: email });
          setUserProperties({ "Last Login At": new Date().toISOString() });
          trackEvent("User Logged In", {
            Method: "Email",
            "Is First Login": isFirstLogin,
          });

          toast({
            title: t("welcomeBack"),
            description: t("signedInSuccess"),
          });

          // Restore the pendingAuthUpload ID if it was saved earlier
          if (existingPendingId && !pendingUploadId) {
            sessionStorage.setItem("pendingAuthUpload", existingPendingId);
          }

          // Call onSuccess and redirect to dashboard instead of checking returnAfterAuthTo
          onSuccess();
          window.location.href = `${window.location.origin}/dashboard?pendingUpload=true`;
          return;
        } else {
          throw new Error("Failed to establish session");
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      const errorMessage = error instanceof Error ? error.message : t("error");
      toast({
        variant: "destructive",
        title: t("authError"),
        description: errorMessage,
      });
      trackApiErrorToast(
        mode === "signup" ? "auth/signup" : "auth/signin",
        errorMessage,
        // @ts-expect-error Supabase errors might have a status
        error?.status
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {mode === "signup" ? t("createNewAccount") : t("loginToAccount")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === "signup"
              ? t("signUpForMenuTranslation")
              : t("loginForMenuTranslation")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 mt-4">
          {/* Google Auth Button - Now as primary method */}
          <GoogleAuthButton
            mode={mode}
            className="w-full"
            pendingUploadId={pendingUploadId}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">
                {t("orContinueWith")}
              </span>
            </div>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    mode === "signin"
                      ? t("enterYourPassword")
                      : t("createPassword")
                  }
                  autoComplete={
                    mode === "signin" ? "current-password" : "new-password"
                  }
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={
                    showPassword ? t("hidePassword") : t("showPassword")
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {mode === "signin" && (
                <Link
                  href="/reset-password"
                  className="text-sm text-muted-foreground hover:text-foreground hover:underline underline-offset-4 text-right block"
                >
                  {t("forgotPassword")}
                </Link>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading
                ? t("loading")
                : mode === "signup"
                ? t("signup")
                : t("login")}
            </Button>

            {mode === "signup" && (
              <p className="text-xs text-muted-foreground text-center">
                {t("termsAgreement")}{" "}
                <Link
                  href="/terms"
                  className="font-medium underline underline-offset-4 hover:text-primary"
                  target="_blank"
                >
                  {t("termsOfService")}
                </Link>{" "}
                {t("and")}{" "}
                <Link
                  href="/privacy-policy"
                  className="font-medium underline underline-offset-4 hover:text-primary"
                  target="_blank"
                >
                  {t("privacyPolicy")}
                </Link>
              </p>
            )}
          </form>

          <div className="text-center text-sm">
            {mode === "signup" ? (
              <p>
                {t("alreadyHaveAccount")}{" "}
                <button
                  type="button"
                  className="underline underline-offset-4 hover:text-primary"
                  onClick={() => setMode("signin")}
                >
                  {t("login")}
                </button>
              </p>
            ) : (
              <p>
                {t("dontHaveAccount")}{" "}
                <button
                  type="button"
                  className="underline underline-offset-4 hover:text-primary"
                  onClick={() => setMode("signup")}
                >
                  {t("signup")}
                </button>
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
